import { useMutation, useQueryClient } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient.ts';
import { Database } from '@/_constants/database.types';

type PaperAttempt = Database['public']['Tables']['past_paper_attempts']['Row'];

interface CreateAttemptParams {
  authId: string;
  pastPaperId: string;
}

interface CreateAttemptResponse {
  message: string;
  attempt_id: string;
  question_count: number;
}

const useCreateAttempt = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ authId, pastPaperId }: CreateAttemptParams): Promise<PaperAttempt> => {
      // Call the edge function to create attempt and answer rows server-side
      const response = await supabaseClient.functions.invoke('create-paper-attempt', {
        body: {
          auth_id: authId,
          past_paper_id: pastPaperId,
        },
      });

      // Check if the response contains an error (status code or explicit error)
      if (response.error) {
        const errorMessage = (response.data as any)?.errors?.[0] || response.error.message;
        if (response.error?.context?.status === 429) {
          throw new Error("You've reached your daily limit of 1 Past Papers. Your limit will reset at midnight.");
        }
        throw new Error(errorMessage);
      }

      const { data } = response.data;

      const responseData = data as CreateAttemptResponse;
      console.log(`✅ Paper attempt created: ${responseData.attempt_id} with ${responseData.question_count} questions`);

      // Fetch the created attempt to return
      const { data: attempt, error: fetchError } = await supabaseClient
        .from('past_paper_attempts')
        .select()
        .eq('id', responseData.attempt_id)
        .single();

      if (fetchError) throw fetchError;

      return attempt;
    },
    onSuccess: (data) => {
      // Invalidate attempts list to refetch
      queryClient.invalidateQueries({ queryKey: ['past_paper_attempts', data.past_paper_id, data.auth_id] });
    },
  });
};

export default useCreateAttempt;
